// Inline error+retry notice. Lives in shared/ui so every page / widget that
// needs a "this query failed — click here to refetch" affordance renders
// the same chrome (border color, copy density, retry button size). Use this
// instead of bespoke per-page error blocks so the app keeps a single error
// voice and Lighthouse contrast targets stay green.

import { cn } from "../lib/cn";

import { Button } from "./button";

export interface RetryNoticeProps {
  /**
   * Short headline. Sentence case. Required so the notice is never a bare
   * retry button without context.
   */
  title: string;
  /** Optional body. Friendly copy — never a stack trace. */
  body?: string;
  /** Retry button label. Defaults to "Retry". */
  retryLabel?: string;
  /**
   * Click handler — almost always a query.refetch() or mutation.reset()
   * passthrough.
   */
  onRetry: () => void;
  /**
   * `inline` (default) renders compact for embedding inside a list / panel.
   * `block` centers the content for use as the only thing on a page.
   */
  variant?: "inline" | "block";
  className?: string;
}

export function RetryNotice({
  title,
  body,
  retryLabel = "Retry",
  onRetry,
  variant = "inline",
  className,
}: RetryNoticeProps) {
  const isBlock = variant === "block";
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col gap-3 rounded-lg border border-destructive/40 bg-background",
        isBlock ? "items-center p-6 text-center" : "p-4",
        className,
      )}
    >
      <h2 className={cn("font-semibold", isBlock ? "text-lg" : "text-sm")}>
        {title}
      </h2>
      {body ? (
        <p
          className={cn(
            "text-muted-foreground",
            isBlock ? "text-sm" : "text-xs",
          )}
        >
          {body}
        </p>
      ) : null}
      <div className={cn(isBlock ? "" : "self-start")}>
        <Button onClick={onRetry} variant="outline" size="sm">
          {retryLabel}
        </Button>
      </div>
    </div>
  );
}
